import { distribuicaoMensal, pedeReajuste } from '@favo/core';
import * as React from 'react';
import { Link, Navigate } from 'react-router-dom';

import { Celula } from '@/componentes/celula';
import { Button } from '@/componentes/ui/button';
import { Skeleton } from '@/componentes/ui/skeleton';
import type { Analise, Diagnostico } from '@/dados';
import {
  mensagemDoErro,
  useAnalise,
  useDiagnosticos,
  useIntegrador,
  useProjetoAtual,
} from '@/dados';
import { formatarData } from '@/design/formato';

import { useArquivamento } from './arquivamento';
import { Capa } from './capa';
import { Comparativo } from './comparativo';
import { Curva } from './curva';
import { CascataDoLaudo, DreDoLaudo } from './memoria';
import { CSS } from './estilo';
import { Premissas } from './premissas';
import { Recomendacoes } from './recomendacoes';
import { SecaoIndicadores } from './secao-indicadores';
import { volumeNecessario } from './volume-necessario';

/* Relatório — o documento de diagnóstico, feito para sair em PDF.
 *
 * DUAS REGRAS ORGANIZAM ESTA PASTA:
 *
 * 1. O VEREDITO É UM SÓ. Selo, capa, tabela, gráfico e recomendações saem
 *    todos de `recomendacao.estado`, que vem pronto do servidor (o mesmo
 *    `recomendar` que o painel usa). Não existe um segundo julgamento aqui: se
 *    o painel diz "acima", este documento não tem como dizer outra coisa. Só o
 *    módulo `veredito.tsx` traduz estado em tom e texto, e ele é o único.
 *
 * 2. REGRA DE OURO. No estado 'acima' nenhum preço menor aparece em lugar
 *    nenhum da página. Não é vigilância manual: `RecomendacaoAcima` não expõe
 *    reajuste nem redução, a coluna de comparação da tabela só recebe o preço
 *    mínimo no estado 'abaixo', a curva do gráfico idem, e a coluna de volume
 *    necessário trabalha sempre com o preço ATUAL. A folga é poder de
 *    negociação, não erro a corrigir.
 *
 * Um módulo por CAPÍTULO do documento (capa, indicadores, comparativo, curva,
 * recomendações, premissas), mais os compartilhados (`frases`, `grandezas`,
 * `veredito`) e o que não é capítulo (`arquivamento`, `estilo`). Este arquivo
 * só decide O QUE mostrar; nenhum capítulo é desenhado aqui dentro.
 *
 * O que NÃO entra: o bloco de CTA do relatório antigo ("Refaça a análise com
 * seus custos reais" / consultoria). Aquilo é linguagem do funil de captação,
 * que é outro produto. Aqui a pessoa acabou de configurar os custos reais em
 * cinco etapas, e o documento termina como laudo. */

export function Relatorio() {
  const analise = useAnalise();
  const integrador = useIntegrador();
  const projeto = useProjetoAtual();
  const diagnosticos = useDiagnosticos();

  // O nome do arquivo que o navegador sugere ao salvar em PDF sai do title.
  // Sem isto, todo laudo salvo recebia o mesmo nome e o integrador ficava com
  // uma pasta de arquivos indistinguíveis.
  const nomeProjeto = projeto.data?.nome_projeto?.trim();
  const nomeEmpresa = integrador.data?.nome_empresa?.trim();
  React.useEffect(() => {
    const anterior = document.title;
    const partes = ['Laudo de precificação', nomeProjeto, nomeEmpresa].filter(Boolean);
    document.title = partes.join(' · ');
    return () => {
      document.title = anterior;
    };
  }, [nomeProjeto, nomeEmpresa]);

  if (analise.isPending) return <Esqueleto />;

  if (analise.isError) {
    return (
      <Falha
        titulo="Não foi possível montar o relatório."
        texto={mensagemDoErro(analise.error)}
        acao={
          <Button variant="outline" onClick={() => void analise.refetch()}>
            Tentar de novo
          </Button>
        }
      />
    );
  }

  // Sem projeto não há o que relatar: a análise é sobre um projeto, e o lugar
  // de escolher ou criar um é o painel.
  if (!analise.data || analise.data.projeto_id === null) return <Navigate to="/" replace />;

  return (
    <Documento
      analise={analise.data}
      empresa={integrador.data?.nome_empresa ?? null}
      nomeProjeto={projeto.data?.nome_projeto ?? null}
      arquivados={diagnosticos.data}
      arquivadosCarregando={diagnosticos.isPending}
    />
  );
}

function Documento({
  analise,
  empresa,
  nomeProjeto,
  arquivados,
  arquivadosCarregando,
}: {
  analise: Analise;
  empresa: string | null;
  nomeProjeto: string | null;
  arquivados: Diagnostico[] | undefined;
  arquivadosCarregando: boolean;
}) {
  const p = analise.parametros;
  const rec = analise.recomendacao;
  const atual = analise.cenarioAtual;
  const dist = analise.distribuicao;

  const emitidoEm = React.useMemo(() => new Date().toISOString(), []);

  // A curva do preço reajustado só existe onde existe reajuste a fazer.
  const distSugerido = React.useMemo(
    () => (pedeReajuste(rec) ? distribuicaoMensal(p, rec.sugerido) : null),
    [p, rec],
  );
  const necessario = React.useMemo(() => volumeNecessario(p, atual), [p, atual]);

  const erroArquivo = useArquivamento({ analise, emitidoEm, arquivados, arquivadosCarregando });

  return (
    <div className="laudo">
      <style>{CSS}</style>

      <div className="acoes" data-nao-imprimir="">
        <Button asChild variant="outline">
          <Link to="/">Voltar à análise</Link>
        </Button>
        <Button onClick={() => window.print()}>Imprimir ou salvar em PDF</Button>
      </div>

      <Capa
        p={p}
        rec={rec}
        atual={atual}
        empresa={empresa}
        nomeProjeto={nomeProjeto}
        emitidoEm={emitidoEm}
      />

      {/* A prova vem logo depois da tese: o veredito da capa afirma, e estes
          dois capítulos mostram de onde sai o número, antes de qualquer
          projeção. */}
      <CascataDoLaudo p={p} atual={atual} />

      <DreDoLaudo p={p} atual={atual} semPreco={p.ticketOrigem !== 'projeto'} />

      <SecaoIndicadores p={p} atual={atual} dist={dist} />

      <Comparativo p={p} rec={rec} atual={atual} necessario={necessario} />

      <Curva p={p} dist={dist} distSugerido={distSugerido} />

      <Recomendacoes p={p} rec={rec} atual={atual} />

      {/* As premissas fecham o documento em vez de abri-lo: são a base de
          consulta, não a conclusão. Quem recebe o laudo quer o veredito, a
          projeção e o que fazer; a ficha de parâmetros é o que ele confere
          depois, se discordar de algum número. */}
      <Premissas p={p} />

      <footer className="rodape">
        <span>Gerado em {formatarData(emitidoEm)} pela ferramenta de precificação FAVO.</span>
        {erroArquivo ? (
          <span className="text-negativo">
            O diagnóstico não foi arquivado: {erroArquivo} O documento acima continua válido.
          </span>
        ) : null}
      </footer>
    </div>
  );
}

function Esqueleto() {
  return (
    <div className="favo mx-auto w-full max-w-laudo">
      <Skeleton className="h-9 w-64" />
      <Skeleton className="h-56 w-full" />
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-72 w-full" />
    </div>
  );
}

/* Único estado de recado do laudo, e ele é sempre falha: sem projeto a tela
   redireciona ao painel, e não existe laudo vazio. Campo telha, o mesmo das
   outras nove telas. */
function Falha({
  titulo,
  texto,
  acao,
}: {
  titulo: string;
  texto: string;
  acao?: React.ReactNode;
}) {
  return (
    <Celula
      campo="telha"
      respiro="folgado"
      role="alert"
      className="mx-auto grid max-w-2xl justify-items-center gap-2 py-12 text-center"
    >
      <p className="titulo-bloco">{titulo}</p>
      <p className="apoio">{texto}</p>
      <div className="flex gap-2 pt-2">
        {acao}
        <Button asChild variant="ghost">
          <Link to="/">Voltar à análise</Link>
        </Button>
      </div>
    </Celula>
  );
}
